import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import * as Styled from './PreFormContent.styles';

import { DEFAULT_TRUNCATE_HEIGHT } from '+/components/Truncate/Truncate.component';
import { FORM_TOP_PADDING } from '+/components/FormWrapper/FormWrapper.styles';
import { TRUNCATE_BUTTON_HEIGHT } from '+/components/Truncate/Truncate.styles';
import * as FormStyled from '+/styles/styledComponents/Form.styles';
import {
  Markdown,
  PETITION_TOP_MARGIN,
} from '+/components/EveryActionPetitionForm/EveryActionPetitionForm.styles';
import { Progress } from '+/components/Progress';
import { Truncate } from '+/components/Truncate';
import { useEveryActionFormProgress } from '+/utils/hooks/useEveryActionFormProgress';
import { useOnScreen } from '+/utils/useOnScreen';
import { useIsMobile } from '+/utils/responsive';
import { getOrCreateRootIfNotExists } from '+/components/Modal/ModalPortal.component';
import { Button } from '+/components/Button';
import { t } from '+/utils/textDictionary';
import scrollToLocation from '+/utils/scrollToLocation';

export const PreFormContent = ({
  formId,
  progressBarStatus,
  textAboveProgressBar,
  progressBarValueLabel,
  progressBarTargetLabel,
  textBelowProgressBar,
  formContentRef,
  formHeaderRef,
}: PreFormContentProps) => {
  const isMobile = useIsMobile();

  // Get progress bar status and set animations
  const progress = useEveryActionFormProgress(formId);
  const progressRef = useRef(null);
  const isProgressRefOnScreen = useOnScreen(progressRef, 0, 0.05, true);
  const [isProgressLoaded, setIsProgressLoaded] = useState(false);
  const hasProgressBar = ['enabled', 'placeholder'].includes(progressBarStatus);
  const progressBarEnabled =
    progressBarStatus === 'enabled' && progress.totalSubmissionProgress >= 2000;
  const progressOnScreen = hasProgressBar ? isProgressRefOnScreen : false;

  useEffect(() => {
    if (progress.loaded) {
      setIsProgressLoaded(true);
    }
  }, [progress]);

  // Compute dynamic issue text height based on other form pre-content elements
  const textAboveProgressBarRef = useRef(null);
  const getRefHeight = ref => (ref.current ? ref.current.offsetHeight : 0);

  const issueTextHeight = isMobile
    ? DEFAULT_TRUNCATE_HEIGHT
    : `calc(100vh - ${FORM_TOP_PADDING} - ${
        FormStyled.FORM_NOTCH_HEIGHTS.center
      } - ${PETITION_TOP_MARGIN} - ${TRUNCATE_BUTTON_HEIGHT} - ${getRefHeight(
        textAboveProgressBarRef,
      )}px - ${getRefHeight(formHeaderRef)}px - ${getRefHeight(progressRef)}px)`;

  // Resolve the portal target after mount so it binds to Page's styled <FixedBannerTarget> rather than
  // racing render-time and minting an unstyled fallback div.
  const [bannerTarget, setBannerTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setBannerTarget(getOrCreateRootIfNotExists('fixed-banner-target'));
  }, []);

  // Determine whether mobile CTA button should appear
  const textBelowProgressBarRef = useRef(null);
  const textBelowProgressBarOnScreen = useOnScreen(textBelowProgressBarRef, 0, 0.2, false);
  const formContentOnScreen = useOnScreen(formContentRef, 0, 0, false);

  // Use text height to estimate whether the truncate text is collapsed, as we don't have direct access to that information
  const truncateTextCollapsed =
    getRefHeight(textBelowProgressBarRef) <= DEFAULT_TRUNCATE_HEIGHT + 70;

  // Add window resize event listener to enable dynamic issue text height to re-compute on resizing window
  const [, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : undefined,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleResize = (): void => setWindowWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <FormStyled.PreContent>
        <FormStyled.Container>
          <FormStyled.Section>
            <div ref={textAboveProgressBarRef} style={{ overflow: 'auto' }}>
              <Markdown progressBarEnabled={progressBarEnabled}>
                {textAboveProgressBar || ''}
              </Markdown>
            </div>
            {hasProgressBar ? (
              <div ref={progressRef} style={{ overflow: 'auto' }}>
                <Progress
                  value={progress.totalSubmissionProgress}
                  valueLabel={progressBarValueLabel}
                  total={progress.loaded && progress.targetAmount}
                  totalLabel={progressBarTargetLabel}
                  isOnScreen={progressOnScreen && isProgressLoaded}
                  animationDuration={2}
                  isEnabled={progressBarEnabled}
                />
              </div>
            ) : (
              <br />
            )}

            <Styled.IssueText ref={textBelowProgressBarRef}>
              <Truncate initialHeight={issueTextHeight} minHeight={80}>
                <Markdown>{textBelowProgressBar || ''}</Markdown>
              </Truncate>
            </Styled.IssueText>
          </FormStyled.Section>
        </FormStyled.Container>
      </FormStyled.PreContent>
      {bannerTarget &&
        ReactDOM.createPortal(
          <Styled.PetitionCTA
            isShown={textBelowProgressBarOnScreen && !truncateTextCollapsed && !formContentOnScreen}
          >
            <Button onClick={() => scrollToLocation(formContentRef.current, 0, 'down')} big>
              {t('EAPetitionForm.CTAText')}
            </Button>
          </Styled.PetitionCTA>,
          bannerTarget,
        )}
    </>
  );
};
